import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { History, Info, Plus, Sparkles } from 'lucide-react';
import {
  DECLINE_REASONS,
  INTAKE_TONES,
  TONE_TEXT,
  intakeSettingsSchema,
  renderIntakeSections,
  resolveIntake,
  type DeclineReason,
  type IntakeSettings,
  type IntakeTone,
  type PiiEntity,
  type PiiEntityInfo,
  type ResolvedIntake,
  type WelcomeMode,
} from '@esa/shared';
import { api } from '../../lib/api.js';
import { Badge, Button, Callout, Checkbox, Field, Input, Panel, Select, Spinner, Switch, Textarea } from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';
import { draftStore, useUnsavedChanges } from '../../lib/unsaved.js';
import { TestAssistantPanel } from './TestAssistantPanel.js';

/**
 * Configuration → Intake (INTAKE-MIGRATION-PLAN.md, stage 1).
 *
 * How the intake assistant treats this desk's mail: what the desk handles,
 * how replies sound, what a decline says, what is always left for a person,
 * who may email it, and which personal-data policy applies.
 *
 * THE PREVIEW IS THE POINT. These fields become part of the assistant's
 * prompt, and the panel at the bottom shows exactly the text they produce,
 * live, as it is typed. The rest of the prompt — how to use tools, never to
 * email an internal note — is the platform's and is not shown or editable
 * here, which is why nothing typed on this screen can remove it.
 *
 * Lists are one item per line: the shape a person already writes a list in.
 */

interface Payload {
  groupName: string;
  desk: string;
  configured: IntakeSettings;
  resolved: ResolvedIntake;
  preview: string;
  piiTypes: (PiiEntityInfo & { key: PiiEntity })[];
  /** The mailboxes that read into this group, and their mode. */
  mailboxes: { address: string; mode: 'off' | 'shadow' | 'on' }[];
  /** Fields this desk requires when a ticket is closed. */
  closeFields: { key: string; label: string; options: string[] | null }[];
  versions: { version: number; at: string; actorName: string | null; intake: IntakeSettings | null }[];
}

export interface Draft {
  summary: string;
  handles: string;
  doesNotHandle: string;
  examples: string;
  tone: IntakeTone;
  customTone: string;
  signOff: string;
  notes: string;
  declines: Record<DeclineReason, string>;
  languageFilter: boolean;
  partialAnswers: boolean;
  neverAnswer: string;
  ackEnabled: boolean;
  ackText: string;
  joinOpen: boolean;
  avoidRepeats: boolean;
  repliesEnabled: boolean;
  closeWhenDone: boolean;
  answerFollowUps: boolean;
  welcomeMode: WelcomeMode;
  welcomeText: string;
  answerFields: Record<string, string>;
  domains: string;
  allowedPii: PiiEntity[];
}

const REASON_LABEL: Record<DeclineReason, { title: string; hint: string }> = {
  personalData: {
    title: 'Contains personal data',
    hint: 'A bank, passport, licence or ID number the personal-data policy does not allow.',
  },
  nsfw: { title: 'Inappropriate language', hint: 'Language that should not go onto a ticket.' },
  outOfScope: {
    title: 'Not something this desk handles',
    hint: 'Outside the scope above. Say what the desk does cover, so the reader can tell why.',
  },
};

const TONE_LABEL: Record<IntakeTone, string> = {
  friendly: 'Friendly',
  empathetic: 'Empathetic',
  neutral: 'Neutral',
  concise: 'Concise',
  formal: 'Formal',
  technical: 'Technical',
  custom: 'Custom — describe it',
};

const toLines = (xs: readonly string[] | undefined) => (xs ?? []).join('\n');
const fromLines = (s: string) =>
  s
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);

export function draftOf(c: IntakeSettings): Draft {
  return {
    summary: c.scope?.summary ?? '',
    handles: toLines(c.scope?.handles),
    doesNotHandle: toLines(c.scope?.doesNotHandle),
    examples: toLines(c.scope?.examples),
    tone: c.persona?.tone ?? 'friendly',
    customTone: c.persona?.customTone ?? '',
    signOff: c.persona?.signOff ?? '',
    notes: c.persona?.notes ?? '',
    declines: {
      personalData: c.declines?.personalData ?? '',
      nsfw: c.declines?.nsfw ?? '',
      outOfScope: c.declines?.outOfScope ?? '',
    },
    languageFilter: c.languageFilter ?? true,
    partialAnswers: c.partialAnswers ?? true,
    neverAnswer: toLines(c.neverAnswer),
    ackEnabled: c.acknowledge?.enabled ?? true,
    ackText: c.acknowledge?.text ?? '',
    joinOpen: c.history?.joinOpen ?? true,
    avoidRepeats: c.history?.avoidRepeats ?? true,
    repliesEnabled: c.replies?.enabled ?? true,
    closeWhenDone: c.replies?.closeWhenDone ?? true,
    answerFollowUps: c.replies?.answerFollowUps ?? false,
    welcomeMode: c.replies?.welcome?.mode ?? 'guided',
    welcomeText: c.replies?.welcome?.text ?? '',
    answerFields: { ...(c.answerFields ?? {}) },
    domains: toLines(c.allowedSenderDomains),
    allowedPii: c.allowedPii ?? [],
  };
}

/**
 * What the form shows when the saved settings arrive or change: the server's,
 * unless the form holds edits — `previous` is the saved settings the form
 * last matched — which are never thrown away by a refetch.
 */
export function followServer(cur: Draft | null, fresh: Draft, previous: string | null): Draft {
  return cur === null || JSON.stringify(settingsOf(cur)) === previous ? fresh : cur;
}

/** The draft as it will be saved. Blanks are omitted, so they fall back to defaults. */
export function settingsOf(d: Draft): IntakeSettings {
  const out: IntakeSettings = {};
  const scope = {
    ...(d.summary.trim() ? { summary: d.summary.trim() } : {}),
    ...(fromLines(d.handles).length ? { handles: fromLines(d.handles) } : {}),
    ...(fromLines(d.doesNotHandle).length ? { doesNotHandle: fromLines(d.doesNotHandle) } : {}),
    ...(fromLines(d.examples).length ? { examples: fromLines(d.examples) } : {}),
  };
  if (Object.keys(scope).length) out.scope = scope;
  out.persona = {
    tone: d.tone,
    ...(d.tone === 'custom' ? { customTone: d.customTone.trim() } : {}),
    ...(d.signOff.trim() ? { signOff: d.signOff.trim() } : {}),
    ...(d.notes.trim() ? { notes: d.notes.trim() } : {}),
  };
  const declines: Partial<Record<DeclineReason, string>> = {};
  for (const r of DECLINE_REASONS) if (d.declines[r].trim()) declines[r] = d.declines[r].trim();
  if (Object.keys(declines).length) out.declines = declines;
  // Saved only when off, so an untouched desk keeps the default.
  if (!d.languageFilter) out.languageFilter = false;
  if (!d.partialAnswers) out.partialAnswers = false;
  if (fromLines(d.neverAnswer).length) out.neverAnswer = fromLines(d.neverAnswer);
  // Saved only when it differs from the default (on, default wording), so an
  // untouched desk keeps following the default if it ever changes.
  if (!d.ackEnabled || d.ackText.trim()) {
    out.acknowledge = { ...(d.ackEnabled ? {} : { enabled: false }), ...(d.ackText.trim() ? { text: d.ackText.trim() } : {}) };
  }
  if (!d.joinOpen || !d.avoidRepeats) {
    out.history = { ...(d.joinOpen ? {} : { joinOpen: false }), ...(d.avoidRepeats ? {} : { avoidRepeats: false }) };
  }
  // The same rule: only what differs from the defaults (on, close when done,
  // a written reply to the default guidance).
  const welcome = {
    ...(d.welcomeMode !== 'guided' ? { mode: d.welcomeMode } : {}),
    ...(d.welcomeText.trim() ? { text: d.welcomeText.trim() } : {}),
  };
  if (!d.repliesEnabled || !d.closeWhenDone || d.answerFollowUps || Object.keys(welcome).length) {
    out.replies = {
      ...(d.repliesEnabled ? {} : { enabled: false }),
      ...(d.closeWhenDone ? {} : { closeWhenDone: false }),
      ...(d.answerFollowUps ? { answerFollowUps: true } : {}),
      ...(Object.keys(welcome).length ? { welcome } : {}),
    };
  }
  const answerFields = Object.fromEntries(
    Object.entries(d.answerFields)
      .map(([k, v]) => [k, v.trim()] as const)
      .filter(([, v]) => v),
  );
  if (Object.keys(answerFields).length) out.answerFields = answerFields;
  if (fromLines(d.domains).length) out.allowedSenderDomains = fromLines(d.domains);
  if (d.allowedPii.length) out.allowedPii = d.allowedPii;
  return out;
}

const when = (iso: string) => new Date(iso).toLocaleString();

/**
 * Whether these settings are in use, from the group's own mailboxes (Mail
 * tab). Nothing to say when one is On: that is the normal state.
 */
function MailboxStatus({ mailboxes }: { mailboxes: Payload['mailboxes'] }) {
  const on = mailboxes.filter((m) => m.mode === 'on');
  const shadow = mailboxes.filter((m) => m.mode === 'shadow');
  const list = (xs: Payload['mailboxes']) => xs.map((m) => m.address).join(', ');
  if (on.length > 0) return null;
  const [title, text] =
    mailboxes.length === 0
      ? [
          'No mailbox yet',
          'No mailbox reads into this group, so nothing uses these settings yet. Add the desk mailbox on the Mail tab. Test the assistant below works either way.',
        ]
      : shadow.length > 0
        ? [
            'Shadow mode',
            `${list(shadow)} is read in shadow: each message is judged with these settings and the outcome recorded, but no ticket is opened and nothing is sent. Switch it On on the Mail tab.`,
          ]
        : [
            'Mailbox off',
            `${list(mailboxes)} is not being read, so nothing uses these settings yet. Switch it to Shadow or On on the Mail tab.`,
          ];
  return (
    <Callout tone="info" icon={<Info />} title={title}>
      <p className="text-xs leading-relaxed">{text}</p>
    </Callout>
  );
}

/** The Select has no empty option, so "not set" is a value of its own. */
const NOT_SET = '__not_set__';

interface ScopeSuggestion {
  text: string;
  tickets: number;
  source: 'tickets' | 'request_type';
  subject: string | null;
}

interface NotHandledSuggestion {
  text: string;
  item: string;
  desk: string;
}

interface Suggestions {
  handles: ScopeSuggestion[];
  doesNotHandle: NotHandledSuggestion[];
}

/** A list with its own "Suggest" button; each row adds one line to a field. */
function SuggestBox({
  label,
  blurb,
  rows,
  loading,
  failed,
  onAsk,
  onAdd,
  empty,
}: {
  label: string;
  blurb: string;
  rows: { key: string; text: string; meta: ReactNode }[] | null;
  loading: boolean;
  failed: boolean;
  onAsk: () => void;
  onAdd: (text: string) => void;
  empty: string;
}) {
  return (
    <div className="rounded-md border border-line-subtle bg-surface-sunken/40 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" leading={<Sparkles />} onClick={onAsk} loading={loading}>
          {label}
        </Button>
        <span className="text-2xs text-ink-tertiary">{blurb}</span>
      </div>
      {failed && <p className="mt-2 text-2xs text-danger">Suggestions could not be loaded.</p>}
      {rows && rows.length === 0 && <p className="mt-2 text-2xs text-ink-tertiary">{empty}</p>}
      {rows && rows.length > 0 && (
        <ul className="mt-2 space-y-1">
          {rows.map((r) => (
            <li key={r.key} className="flex flex-wrap items-center gap-2 text-2xs">
              <Button
                size="xs"
                variant="ghost"
                leading={<Plus />}
                onClick={() => onAdd(r.text)}
                aria-label={`Add ${r.text}`}
              >
                Add
              </Button>
              <span className="text-ink">{r.text}</span>
              {r.meta}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Wording compared loosely — the same rule the server uses (`scopeKey`). */
const looseKey = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

export function IntakeTab({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-intake', tenant],
    queryFn: () => api.get<Payload>(`/api/v1/tenants/${tenant}/admin/intake`),
    enabled: Boolean(tenant),
  });

  const [draft, setDraft] = useState<Draft | null>(null);
  const [shown, setShown] = useState({ handles: false, doesNotHandle: false });
  const suggestions = useQuery({
    queryKey: ['admin-intake-suggestions', tenant],
    queryFn: () => api.get<Suggestions>(`/api/v1/tenants/${tenant}/admin/intake/suggestions`),
    enabled: Boolean(tenant) && (shown.handles || shown.doesNotHandle),
  });
  const ask = (which: 'handles' | 'doesNotHandle') => {
    if (shown[which]) void suggestions.refetch();
    else setShown({ ...shown, [which]: true });
  };
  /**
   * EDITS ARE NOT OVERWRITTEN BY A REFETCH. This replaced the form with what
   * the server had whenever the saved settings changed — someone else's save,
   * any refetch that differed — and silently threw away whatever was being
   * typed. Now the form follows the server only while it has no edits.
   */
  const baseline = useRef<string | null>(null);
  useEffect(() => {
    if (!q.data) return;
    const fresh = draftOf(q.data.configured);
    // Captured now: the updater below runs later, after the baseline moves on.
    const previous = baseline.current;
    setDraft((cur) => followServer(cur, fresh, previous));
    baseline.current = JSON.stringify(settingsOf(fresh));
  }, [q.data]);

  /**
   * A DRAFT KEPT IN THIS BROWSER. Leaving the page any way at all — a link,
   * a reload, a closed tab — used to lose unsaved settings. While there are
   * edits they are kept here, and offered back on return.
   */
  const storeKey = `tributary:intake-draft:${tenant}`;
  const [offer, setOffer] = useState<{ value: Draft; savedAt: string } | null>(null);
  const offerChecked = useRef(false);
  useEffect(() => {
    if (!q.data || offerChecked.current) return;
    offerChecked.current = true;
    const kept = draftStore.read<Draft>(storeKey);
    if (kept && JSON.stringify(settingsOf(kept.value)) !== JSON.stringify(settingsOf(draftOf(q.data.configured)))) setOffer(kept);
    else draftStore.clear(storeKey);
  }, [q.data, storeKey]);

  const built = useMemo(() => (draft ? settingsOf(draft) : null), [draft]);
  const savedKey = q.data ? JSON.stringify(settingsOf(draftOf(q.data.configured))) : null;
  const isDirty = Boolean(built && savedKey !== null && JSON.stringify(built) !== savedKey);
  useUnsavedChanges(isDirty, 'Intake settings');
  useEffect(() => {
    if (offer) return; // not until they have said what to do with the one already kept
    if (isDirty && draft) draftStore.write(storeKey, draft);
    else if (savedKey !== null) draftStore.clear(storeKey);
  }, [isDirty, draft, offer, savedKey, storeKey]);
  const check = useMemo(() => (built ? intakeSettingsSchema.safeParse(built) : null), [built]);

  if (q.isLoading || !draft || !built) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">The intake settings could not be loaded.</p>;

  const s = q.data!;
  const desk = draft.signOff.trim() || `${s.groupName} desk`;
  const live = resolveIntake(built, desk);
  const preview = renderIntakeSections(live, desk);
  const dirty = isDirty;
  const problem = check && !check.success ? check.error.issues[0] : null;
  const problemText = problem ? `${problem.path.join(' › ') || 'settings'}: ${problem.message}` : null;
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });

  const save = (intake: IntakeSettings, done: string) =>
    write.mutateAsync({
      method: 'patch',
      path: `/api/v1/tenants/${tenant}/admin/settings`,
      body: { intake },
      done,
    });


  return (
    <div className="space-y-4">
      {offer && (
        <Callout tone="warn">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs">
              You have unsaved Intake changes for this desk from {when(offer.savedAt)}, kept in this browser.
            </span>
            <span className="ml-auto flex gap-1.5">
              <Button
                size="xs"
                onClick={() => {
                  setDraft(offer.value);
                  setOffer(null);
                }}
              >
                Restore them
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  draftStore.clear(storeKey);
                  setOffer(null);
                }}
              >
                Discard
              </Button>
            </span>
          </div>
        </Callout>
      )}
      <MailboxStatus mailboxes={s.mailboxes ?? []} />

      <Panel
        title="What this desk handles"
        subtitle="The assistant answers and files within this scope, and declines what is outside it."
        action={
          <Button
            onClick={() => void save(built, 'Intake settings saved')}
            disabled={!dirty || Boolean(problem)}
            loading={write.isPending}
          >
            Save
          </Button>
        }
        footer={
          problemText ? (
            <p className="text-2xs text-danger">Cannot save — {problemText}</p>
          ) : (
            <p className="text-2xs leading-relaxed text-ink-tertiary">
              One item per line. Be concrete: &ldquo;VPN and wifi&rdquo; helps the assistant more than
              &ldquo;networking&rdquo;. It judges what is being asked for, not what is mentioned — a
              laptop request that mentions payroll is still a laptop request.
            </p>
          )
        }
      >
        <div className="space-y-2.5">
          <Field label="In a sentence or two" hint="What this desk is, and who it is for.">
            <Textarea
              rows={2}
              value={draft.summary}
              onChange={(e) => set({ summary: e.target.value })}
              placeholder="ESA Tech is the internal IT service desk for ESA staff."
              aria-label="Scope summary"
            />
          </Field>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <Field label="Handles" hint="One per line.">
              <Textarea
                rows={7}
                value={draft.handles}
                onChange={(e) => set({ handles: e.target.value })}
                placeholder={'laptops, monitors and docks\nsoftware installs and licences\nVPN and wifi'}
                aria-label="Handles"
              />
            </Field>
            <Field label="Does not handle" hint="One per line. Say where it goes instead, if you know.">
              <Textarea
                rows={7}
                value={draft.doesNotHandle}
                onChange={(e) => set({ doesNotHandle: e.target.value })}
                placeholder={'pay, benefits and leave (HR)\nbuilding and parking (Facilities)'}
                aria-label="Does not handle"
              />
            </Field>
          </div>
          <div className="grid gap-2.5 sm:grid-cols-2">
            <SuggestBox
              label="Suggest from this desk's tickets"
              blurb="What this desk's resolved tickets show it handles."
              loading={shown.handles && suggestions.isFetching}
              failed={shown.handles && Boolean(suggestions.error)}
              onAsk={() => ask('handles')}
              onAdd={(t) => set({ handles: [...fromLines(draft.handles), t].join(String.fromCharCode(10)) })}
              empty="Nothing more to suggest — everything the tickets show is already listed."
              rows={
                shown.handles && suggestions.data
                  ? (() => {
                      const have = new Set(fromLines(draft.handles).map(looseKey));
                      return suggestions.data.handles
                        .filter((x) => !have.has(looseKey(x.text)))
                        .map((x) => ({
                          key: looseKey(x.text),
                          text: x.text,
                          meta: (
                            <>
                              {x.subject && <span className="text-ink-tertiary">· {x.subject}</span>}
                              {x.source === 'tickets' ? (
                                <Badge tone="info">{x.tickets.toLocaleString()} tickets</Badge>
                              ) : (
                                <Badge tone="neutral">
                                  request type{x.tickets > 0 ? ` · ${x.tickets.toLocaleString()} tickets` : ''}
                                </Badge>
                              )}
                            </>
                          ),
                        }));
                    })()
                  : null
              }
            />
            <SuggestBox
              label="Suggest from other desks"
              blurb="What other groups say they handle — and so where to send it."
              loading={shown.doesNotHandle && suggestions.isFetching}
              failed={shown.doesNotHandle && Boolean(suggestions.error)}
              onAsk={() => ask('doesNotHandle')}
              onAdd={(t) =>
                set({ doesNotHandle: [...fromLines(draft.doesNotHandle), t].join(String.fromCharCode(10)) })
              }
              empty="Nothing to suggest — other desks have not listed anything this desk does not already cover."
              rows={
                shown.doesNotHandle && suggestions.data
                  ? (() => {
                      const strip = (x: string) => looseKey(x.replace(/\s*\([^()]*\)\s*$/, ''));
                      const have = new Set([
                        ...fromLines(draft.doesNotHandle).map(strip),
                        ...fromLines(draft.handles).map(looseKey),
                      ]);
                      return suggestions.data.doesNotHandle
                        .filter((x) => !have.has(looseKey(x.item)))
                        .map((x) => ({
                          key: `${x.desk}:${looseKey(x.item)}`,
                          text: x.text,
                          meta: <Badge tone="neutral">handled by {x.desk}</Badge>,
                        }));
                    })()
                  : null
              }
            />
          </div>
          <Field label="Example requests" hint="Real ones, written the way people write them. One per line.">
            <Textarea
              rows={3}
              value={draft.examples}
              onChange={(e) => set({ examples: e.target.value })}
              placeholder={'My Z drive has disappeared again\nCan I get a second monitor?'}
              aria-label="Example requests"
            />
          </Field>
        </div>
      </Panel>

      <Panel title="How replies sound" subtitle="The assistant's voice for this desk.">
        <div className="space-y-2.5">
          <Field
            label="Tone"
            hint={draft.tone === 'custom' ? 'In your own words, below.' : `Tells the assistant: ${TONE_TEXT[draft.tone]}`}
          >
            <Select
              value={draft.tone}
              onValueChange={(v) => set({ tone: v as IntakeTone })}
              options={INTAKE_TONES.map((t) => ({
                value: t,
                label: TONE_LABEL[t],
                ...(t === 'custom' ? {} : { hint: TONE_TEXT[t] }),
              }))}
              aria-label="Tone"
            />
          </Field>
          {draft.tone === 'custom' && (
            <Field
              label="Describe the tone"
              hint={`How replies should sound, as you would brief a new colleague. ${draft.customTone.length} / 500`}
              required
            >
              <Textarea
                rows={2}
                value={draft.customTone}
                onChange={(e) => set({ customTone: e.target.value })}
                placeholder="Calm and reassuring — people writing to Health and Safety are often worried. Plain words, no jargon."
                aria-label="Custom tone"
              />
            </Field>
          )}
          <Field label="Sign replies as" hint="Also how the desk is named in the default wording below.">
            <Input
              value={draft.signOff}
              onChange={(e) => set({ signOff: e.target.value })}
              placeholder={`${s.groupName} desk`}
              aria-label="Sign replies as"
            />
          </Field>
          <Field label="Anything else about how this desk writes" hint={`${draft.notes.length} / 1000`}>
            <Textarea
              rows={3}
              value={draft.notes}
              onChange={(e) => set({ notes: e.target.value })}
              placeholder="Never promise a time. Point people to the self-service portal for password resets."
              aria-label="Writing notes"
            />
          </Field>
        </div>
      </Panel>

      <Panel
        title="When a message is filed"
        subtitle="When the assistant cannot answer, the request becomes a ticket. This tells the person it arrived."
      >
        <div className="space-y-2.5">
          <Switch
            checked={draft.ackEnabled}
            onCheckedChange={(on) => set({ ackEnabled: on })}
            label="Send an acknowledgement"
          />
          {draft.ackEnabled && (
            <Field
              label="Wording"
              hint="{ticket} becomes the ticket number. Leave blank to use the default shown. A request type can have its own instead (Queues & types, edit the type), for security reports and other requests that need different instructions."
            >
              <Textarea
                rows={3}
                value={draft.ackText}
                onChange={(e) => set({ ackText: e.target.value })}
                placeholder={live.acknowledge.custom ? '' : live.acknowledge.text}
                aria-label="Acknowledgement wording"
              />
            </Field>
          )}
          {!draft.ackEnabled && (
            <p className="text-2xs text-ink-tertiary">Off: a filed request gets no reply until someone at the desk answers it.</p>
          )}
          {/* Their own history, used before a second ticket is opened. */}
          <Switch
            checked={draft.joinOpen}
            onCheckedChange={(on) => set({ joinOpen: on })}
            label="Add a new email to their open request when it is about the same thing"
          />
          <p className="text-2xs text-ink-tertiary">
            Instead of a second ticket. Only when the model is sure; the ticket notes that it was added, and the
            acknowledgement tells them where it went.
          </p>
        </div>
      </Panel>

      <Panel
        title="When someone replies to a resolved ticket"
        subtitle="A reply is read before it reopens anything. A thank-you leaves the ticket resolved, and a reply to a closed ticket joins it instead of opening a new one."
      >
        <div className="space-y-2.5">
          <Switch
            checked={draft.repliesEnabled}
            onCheckedChange={(on) => set({ repliesEnabled: on })}
            label="Read replies before reopening"
          />
          {!draft.repliesEnabled && (
            <p className="text-2xs text-ink-tertiary">
              Off: any reply to a resolved ticket reopens it, and a reply to a closed one opens a new ticket.
            </p>
          )}
          {draft.repliesEnabled && (
            <>
              <Switch
                checked={draft.closeWhenDone}
                onCheckedChange={(on) => set({ closeWhenDone: on })}
                label="Close the ticket when they say it can be closed"
              />
              <Switch
                checked={draft.answerFollowUps}
                onCheckedChange={(on) => set({ answerFollowUps: on })}
                label="Let the assistant answer a follow-up question"
              />
              <p className="text-2xs text-ink-tertiary">
                {draft.answerFollowUps
                  ? 'When someone the assistant answered writes back with a NEW question, it may answer once more, from a different article, and resolve the ticket again. "It still doesn’t work" always goes to a person, and so does anything after the second answer.'
                  : 'Off: any reply that is not a thank-you reopens the ticket for a person.'}
              </p>
              <Field
                label="Reply to a thank-you"
                hint="Sent once per resolution, by email. Anything the model is unsure about reopens the ticket as before."
              >
                <Select
                  value={draft.welcomeMode}
                  onValueChange={(v) => set({ welcomeMode: v as WelcomeMode })}
                  options={[
                    { value: 'guided', label: 'Write one like this (varies each time)' },
                    { value: 'fixed', label: 'Always send this' },
                    { value: 'off', label: "Don't reply" },
                  ]}
                  aria-label="Reply to a thank-you"
                />
              </Field>
              {draft.welcomeMode !== 'off' && (
                <Field
                  label={draft.welcomeMode === 'fixed' ? 'The reply' : 'Guidance for the reply'}
                  hint={
                    draft.welcomeMode === 'fixed'
                      ? 'Sent exactly as written. Leave blank to use the default shown.'
                      : "The model writes a short reply following this, in the desk's tone above. Leave blank to use the default shown."
                  }
                >
                  <Textarea
                    rows={3}
                    value={draft.welcomeText}
                    onChange={(e) => set({ welcomeText: e.target.value })}
                    placeholder={live.replies.welcome.custom ? '' : live.replies.welcome.text}
                    aria-label={draft.welcomeMode === 'fixed' ? 'Thank-you reply' : 'Thank-you reply guidance'}
                  />
                </Field>
              )}
            </>
          )}
        </div>
      </Panel>

      <Panel
        title="When the assistant answers"
        subtitle="An answered ticket is resolved, and nothing is required of it. Anything set here is recorded on it — a resolution code for reporting, say."
      >
        <div className="mb-3 space-y-1">
          <Switch
            checked={draft.partialAnswers}
            onCheckedChange={(on) => set({ partialAnswers: on })}
            label="Answer the parts it can when an email asks for several things"
          />
          <p className="text-2xs text-ink-tertiary">
            {draft.partialAnswers
              ? 'The reply answers what the knowledgebase covers and says the rest is with the team. The ticket stays open, with a note of what is still to do.'
              : 'Off: an email that asks for anything the knowledgebase does not cover waits for a person, whole.'}
          </p>
        </div>
        <div className="mb-3 space-y-1">
          <Switch
            checked={draft.avoidRepeats}
            onCheckedChange={(on) => set({ avoidRepeats: on })}
            label="Don't answer with an article already sent to them in the last 30 days"
          />
          <p className="text-2xs text-ink-tertiary">
            If they are writing again, it did not settle it. The request goes to a person, and the ticket says which
            article was already tried.
          </p>
        </div>
        {(s.closeFields ?? []).length === 0 ? (
          <p className="text-2xs text-ink-tertiary">This desk requires nothing extra when a ticket closes.</p>
        ) : (
          <div className="grid gap-2.5 sm:grid-cols-2">
            {s.closeFields.map((f) => {
              const value = draft.answerFields[f.key] ?? '';
              const put = (v: string) => set({ answerFields: { ...draft.answerFields, [f.key]: v } });
              return (
                <Field
                  key={f.key}
                  label={f.label}
                  hint={value ? 'Recorded on every answered ticket.' : 'Optional. Not set: answered tickets are resolved without it.'}
                >
                  {f.options ? (
                    <Select
                      value={value || NOT_SET}
                      onValueChange={(v) => put(v === NOT_SET ? '' : v)}
                      options={[
                        { value: NOT_SET, label: 'Not set' },
                        ...f.options.map((o) => ({ value: o, label: o })),
                      ]}
                      aria-label={f.label}
                    />
                  ) : (
                    <Input value={value} onChange={(e) => put(e.target.value)} aria-label={f.label} />
                  )}
                </Field>
              );
            })}
          </div>
        )}
      </Panel>

      <Panel
        title="When a message is declined"
        subtitle="No ticket is opened. The person is told why, so they can fix it and send it again."
      >
        <div className="space-y-3">
          <Switch
            checked={draft.languageFilter}
            onCheckedChange={(on) => set({ languageFilter: on })}
            label="Decline inappropriate language"
          />
          {!draft.languageFilter && (
            <p className="text-2xs text-ink-tertiary">
              Off: strong language, slurs and abusive content are filed as written, for a person to read. Personal
              data and scope are still checked.
            </p>
          )}
          {DECLINE_REASONS.filter((r) => r !== 'nsfw' || draft.languageFilter).map((r) => (
            <Field
              key={r}
              label={REASON_LABEL[r].title}
              hint={`${REASON_LABEL[r].hint} Leave blank to use the default shown.`}
            >
              <Textarea
                rows={4}
                value={draft.declines[r]}
                onChange={(e) => set({ declines: { ...draft.declines, [r]: e.target.value } })}
                placeholder={live.customDeclines[r] ? '' : live.declines[r]}
                aria-label={REASON_LABEL[r].title}
              />
            </Field>
          ))}
        </div>
      </Panel>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel
          title="Always leave for a person"
          subtitle="Never answered by the assistant, even with a strong knowledgebase match."
        >
          <Field label="Topics" hint="One per line.">
            <Textarea
              rows={4}
              value={draft.neverAnswer}
              onChange={(e) => set({ neverAnswer: e.target.value })}
              placeholder={'access requests\nanything about a security incident'}
              aria-label="Never answer"
            />
          </Field>
        </Panel>

        <Panel title="Who can email this desk" subtitle="Mail from any other domain is rejected without a reply.">
          <Field label="Sender domains" hint="One per line, like esassoc.com. Blank means esassoc.com.">
            <Textarea
              rows={4}
              value={draft.domains}
              onChange={(e) => set({ domains: e.target.value })}
              placeholder="esassoc.com"
              aria-label="Sender domains"
              className="font-mono"
            />
          </Field>
        </Panel>
      </div>

      <Panel
        title="Personal data"
        subtitle="A message containing any of these is declined and no ticket is opened — unless you allow it here."
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Allow only what this desk&rsquo;s real work needs, such as bank details for a Payroll
            direct-deposit change. Anything allowed is kept on the ticket. Payment card numbers and
            passwords are never allowed: no desk has a reason to hold either, and a password written
            on a ticket is a password shared.
          </p>
        }
      >
        <div className="grid gap-2 sm:grid-cols-2">
          {s.piiTypes.map((t) => (
            <Checkbox
              key={t.key}
              checked={t.allowable && draft.allowedPii.includes(t.key)}
              disabled={!t.allowable}
              onCheckedChange={(on) =>
                set({
                  allowedPii: on
                    ? [...draft.allowedPii, t.key]
                    : draft.allowedPii.filter((k) => k !== t.key),
                })
              }
              label={t.label}
              hint={t.allowable ? `e.g. ${t.example}` : 'Never allowed'}
            />
          ))}
        </div>
        <p className="mt-2 text-2xs text-ink-secondary">
          {draft.allowedPii.length === 0
            ? 'Nothing allowed — every message with personal data is declined.'
            : `Allowed: ${draft.allowedPii.length} of ${s.piiTypes.filter((t) => t.allowable).length}.`}
        </p>
      </Panel>

      <TestAssistantPanel tenant={tenant} intake={built} blocked={problemText} unsaved={dirty} />

      <Panel
        title="What the assistant is told"
        subtitle="This desk's part of the assistant's instructions, exactly as your settings produce it. Updates as you type."
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            The rest of the instructions — how to search, when to file, never to email an internal
            note — are set for the whole platform and cannot be changed from here.
          </p>
        }
      >
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap rounded-md border border-line-subtle bg-surface-sunken/40 p-3 font-mono text-2xs leading-relaxed text-ink-secondary">
          {preview}
        </pre>
      </Panel>

      {s.versions.length > 0 && (
        <Panel title="Earlier versions" subtitle="Restore one to put its settings back exactly.">
          <ul className="space-y-1.5">
            {s.versions.map((v, i) => (
              <li
                key={v.version}
                className="flex flex-wrap items-center gap-2 rounded-md border border-line-subtle p-2 text-2xs"
              >
                <History aria-hidden className="h-3.5 w-3.5 text-ink-tertiary" />
                <span className="text-ink-secondary">
                  {when(v.at)}
                  {v.actorName ? ` · ${v.actorName}` : ''}
                </span>
                {i === 0 ? (
                  <Badge tone="neutral">current</Badge>
                ) : (
                  <span className="ml-auto">
                    <Button
                      size="xs"
                      variant="outline"
                      loading={write.isPending}
                      onClick={() => void save(v.intake ?? {}, `Restored the version from ${when(v.at)}`)}
                    >
                      Restore
                    </Button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}

      {/* ALWAYS IN REACH. The only Save was in the first panel's header, ten
          panels above most of what can be edited — so settings were changed,
          tested, and never saved. */}
      {dirty && (
        <div className="sticky bottom-3 z-20 flex flex-wrap items-center gap-2 rounded-lg border border-warn-line bg-surface p-2.5 shadow-md">
          <span className="text-xs font-medium text-ink">Unsaved changes</span>
          <span className="text-2xs text-ink-secondary">
            {problemText ? `Cannot save — ${problemText}` : 'Nothing here is live until it is saved.'}
          </span>
          <span className="ml-auto flex gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => setDraft(draftOf(s.configured))}>
              Discard
            </Button>
            <Button
              size="sm"
              onClick={() => void save(built, 'Intake settings saved')}
              disabled={Boolean(problem)}
              loading={write.isPending}
            >
              Save
            </Button>
          </span>
        </div>
      )}
    </div>
  );
}
